import React, { useState, useEffect } from 'react';
import { Card, Badge, Divider, Button, Modal, Input, Alert, List, Tag } from 'antd';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Bell,
  BellRing,
  X
} from 'lucide-react';
import { plantApiGet, plantApiPut } from '../../utils/plant_api';
import { defaultColors } from '@/themes/antd-theme';

const { TextArea } = Input;

const SensorAlertPanel = ({ refreshTrigger }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAlerts();
  }, [refreshTrigger]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await plantApiGet('/api/sensors/alerts', {
        status: 'NEW,ACKNOWLEDGED'
      });
      setAlerts(response.data || []);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await plantApiPut(`/api/sensors/alerts/${alertId}/acknowledge`);
      fetchAlerts();
    } catch (err) {
      console.error('Error acknowledging alert:', err);
      setError('Failed to acknowledge alert');
    }
  };

  const handleResolveClick = (alert) => {
    setSelectedAlert(alert);
    setResolveDialogOpen(true);
    setResolutionNotes('');
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;

    try {
      await plantApiPut(`/api/sensors/alerts/${selectedAlert.id}/resolve`, {
        resolution_notes: resolutionNotes
      });
      setResolveDialogOpen(false);
      setSelectedAlert(null);
      setResolutionNotes('');
      fetchAlerts();
    } catch (err) {
      console.error('Error resolving alert:', err);
      setError('Failed to resolve alert');
    }
  };

  const getAlertIcon = (level) => {
    const icons = {
      'CRITICAL': <AlertCircle size={20} style={{ color: defaultColors.error }} />,
      'WARNING': <AlertTriangle size={20} style={{ color: defaultColors.warning }} />,
      'INFO': <Info size={20} style={{ color: defaultColors.info }} />
    };
    return icons[level] || <Info size={20} />;
  };

  const getAlertColor = (level) => {
    const colors = {
      'CRITICAL': 'error',
      'WARNING': 'warning',
      'INFO': 'blue'
    };
    return colors[level] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'error',
      'ACKNOWLEDGED': 'warning',
      'RESOLVED': 'success'
    };
    return colors[status] || 'default';
  };

  const criticalCount = alerts.filter(a => a.alert_level === 'CRITICAL' && a.status !== 'RESOLVED').length;
  const warningCount = alerts.filter(a => a.alert_level === 'WARNING' && a.status !== 'RESOLVED').length;

  return (
    <>
      <Card className="p-4 h-full">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Badge count={criticalCount + warningCount} offset={[0, 0]}>
              <BellRing size={24} style={{ color: defaultColors.text.secondary }} />
            </Badge>
            <h6 className="text-base font-semibold m-0">
              Active Alerts
            </h6>
          </div>
          <div className="flex gap-2">
            <Tag
              icon={<AlertCircle size={14} />}
              color={criticalCount > 0 ? 'error' : 'default'}
            >
              {criticalCount} Critical
            </Tag>
            <Tag
              icon={<AlertTriangle size={14} />}
              color={warningCount > 0 ? 'warning' : 'default'}
            >
              {warningCount} Warning
            </Tag>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            closable
            onClose={() => setError(null)}
            className="mb-4"
          />
        )}

        <Divider className="mb-4" />

        {loading ? (
          <p className="text-sm text-gray-600 text-center">
            Loading alerts...
          </p>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle size={48} style={{ color: defaultColors.success, marginBottom: 16 }} />
            <p className="text-base text-gray-600 mb-1">
              No active alerts
            </p>
            <p className="text-xs text-gray-500 mb-0">
              All systems operating normally
            </p>
          </div>
        ) : (
          <List
            className="max-h-96 overflow-auto"
            dataSource={alerts}
            renderItem={(alert, index) => (
              <List.Item
                key={alert.id}
                className={`flex-col items-start ${alert.status === 'NEW' ? 'bg-gray-50' : ''}`}
              >
                <div className="flex w-full items-start gap-2">
                  <div className="min-w-10 pt-1">
                    {getAlertIcon(alert.alert_level)}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h6 className="text-sm font-semibold m-0">
                          {alert.sensor_details?.sensor_name}
                        </h6>
                        <span className="text-xs text-gray-600">
                          {alert.sensor_details?.sensor_code}
                        </span>
                      </div>
                      <Tag color={getStatusColor(alert.status)}>
                        {alert.status}
                      </Tag>
                    </div>

                    <p className="text-sm text-gray-600 mb-2">
                      {alert.message}
                    </p>

                    <div className="flex gap-2 mb-2 flex-wrap">
                      <Tag
                        color={getAlertColor(alert.alert_level)}
                        bordered
                      >
                        {alert.alert_type}
                      </Tag>
                      {alert.sensor_details?.equipment_details && (
                        <Tag bordered>
                          {alert.sensor_details.equipment_details.equipment_name}
                        </Tag>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 mb-2">
                      Triggered: {new Date(alert.triggered_at).toLocaleString()}
                    </p>

                    <div className="flex gap-2">
                      {alert.status === 'NEW' && (
                        <Button
                          size="small"
                          style={{
                            borderColor: defaultColors.primary,
                            color: defaultColors.primary,
                          }}
                          onClick={() => handleAcknowledge(alert.id)}
                        >
                          Acknowledge
                        </Button>
                      )}
                      {alert.status !== 'RESOLVED' && (
                        <Button
                          size="small"
                          type="primary"
                          style={{
                            backgroundColor: defaultColors.success,
                            borderColor: defaultColors.success,
                          }}
                          onClick={() => handleResolveClick(alert)}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Modal
        title="Resolve Alert"
        open={resolveDialogOpen}
        onCancel={() => setResolveDialogOpen(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setResolveDialogOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="resolve"
            type="primary"
            onClick={handleResolve}
            disabled={!resolutionNotes.trim()}
            style={{
              backgroundColor: defaultColors.success,
              borderColor: defaultColors.success,
            }}
          >
            Resolve Alert
          </Button>
        ]}
      >
        {selectedAlert && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-0">
              Sensor: {selectedAlert.sensor_details?.sensor_name}
            </p>
            <p className="text-sm text-gray-600 mb-0">
              Alert: {selectedAlert.message}
            </p>
            <TextArea
              rows={4}
              placeholder="Describe the actions taken to resolve this alert..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default SensorAlertPanel;
